import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarClock, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import TopBar from "@/components/journey/TopBar";
import SlotPicker from "@/components/journey/SlotPicker";
import PaymentPanel from "@/components/journey/PaymentPanel";

const LABEL = {
  awaiting_payment: "Awaiting payment", payment_submitted: "UPI submitted - to be verified",
  unpaid: "Not paid", paid: "Paid", confirmed: "Confirmed", completed: "Completed",
  cancelled: "Cancelled", expired: "Hold expired",
};

export default function EditBookingPage() {
  const { t, settings } = useApp();
  const [ref, setRef] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState(null); // { token, booking }
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slotKey, setSlotKey] = useState(0);

  useEffect(() => { if (found) { setDate(found.booking.date); setTime(""); } }, [found]);

  const bookingForPay = useMemo(() => found ? { ...found.booking, token: found.token } : null, [found]);

  const [matches, setMatches] = useState(null);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!ref.trim() && !phone.trim()) return toast.error("Please enter your reference ID or your WhatsApp number");
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/lookup", {
        ref: ref.trim() ? ref.trim().toUpperCase() : null,
        phone: phone.trim() ? phone.trim() : null,
      });
      if (data.matches && data.matches.length > 1) {
        setMatches(data.matches);
      } else {
        setFound({ token: data.token, booking: data.booking });
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  const pickMatch = (m) => { setMatches(null); setFound({ token: m.token, booking: m.booking }); };

  const doReschedule = async () => {
    if (!date || !time) return toast.error("Please pick a new date and time");
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${found.booking.id}/self-reschedule`, { token: found.token, date, time });
      toast.success(t("rescheduled"));
      setFound({ token: found.token, booking: data });
      setTime("");
      setSlotKey((k) => k + 1);
      waNotify("UPDATED", data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const doCancel = async () => {
    if (!window.confirm(t("confirmCancel"))) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${found.booking.id}/self-cancel`, { token: found.token });
      toast.success(t("cancelled"));
      setFound({ token: found.token, booking: data });
      waNotify("CANCELLED", data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const onPaid = (data) => {
    setFound({ token: found.token, booking: { ...found.booking, ...data } });
    toast.success("Payment submitted. We'll verify it shortly.");
    waNotify("PAYMENT UPDATED", { ...found.booking, ...data });
  };

  const waNotify = (kind, b) => {
    const statusText = LABEL[b.status] || b.status;
    const lines = [
      `Booking ${kind}`,
      `Ref: ${b.ref}`,
      `Name: ${b.name}`,
      `WhatsApp: ${b.phone}`,
      `Service: ${b.service_label}`,
      `Appointment: ${b.date} at ${b.time}`,
      kind === "CANCELLED" ? "Status: Cancelled by person"
        : kind === "PAYMENT UPDATED" ? `Payment: ${statusText}` : "Please note the new slot.",
    ].join("\n");
    const url = `https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent(lines)}`;
    window.open(url, "_blank");
  };

  const canEdit = found && !["cancelled", "completed", "expired"].includes(found.booking.status);
  const isUnpaid = found && (found.booking.status === "unpaid" || found.booking.status === "awaiting_payment");

  return (
    <div className="relative z-10 min-h-screen">
      <TopBar />
      <main className="mx-auto max-w-xl px-5 py-10">
        <Link to="/" data-testid="back-home-link" className="mb-6 inline-flex items-center gap-1 text-sm text-emerald-100/80 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> {t("back")}
        </Link>
        <section className="glass space-y-5 rounded-[2rem] p-6 sm:p-10" data-testid="edit-booking-page">
          <h1 className="font-display text-2xl text-white">{t("editBookingTitle")}</h1>
          <p className="text-sm text-emerald-100/70">{t("editBookingIntro")}</p>

          {!found && !matches && (
            <form onSubmit={lookup} className="space-y-3">
              <p className="text-xs text-emerald-100/60">Enter either your booking reference ID <b>or</b> your WhatsApp number — one is enough.</p>
              <div className="space-y-1.5">
                <label className="text-xs text-emerald-100/70">{t("bookingRef")}</label>
                <Input data-testid="input-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="JM1A2B3C" className="h-11 rounded-xl border-emerald-300/20 bg-black/20 text-emerald-50" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-emerald-100/70">{t("whatsappNumber")}</label>
                <Input data-testid="input-whatsapp" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91" className="h-11 rounded-xl border-emerald-300/20 bg-black/20 text-emerald-50" />
              </div>
              <Button type="submit" data-testid="lookup-btn" disabled={busy || (!ref.trim() && !phone.trim())} className="h-11 w-full rounded-full bg-emerald-400 text-emerald-950 hover:bg-emerald-300">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("find")}
              </Button>
            </form>
          )}

          {matches && (
            <div className="space-y-3" data-testid="lookup-matches">
              <p className="text-sm text-emerald-100/80">We found {matches.length} bookings for this WhatsApp number. Pick the one you want to change:</p>
              <ul className="space-y-2">
                {matches.map((m) => (
                  <li key={m.booking.id}>
                    <button type="button" onClick={() => pickMatch(m)} data-testid={`match-${m.booking.ref}`}
                            className="w-full rounded-2xl border border-emerald-300/20 bg-black/15 p-3 text-left text-sm text-emerald-50 hover:bg-emerald-300/10">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-semibold">{m.booking.ref}</span>
                        <span className="text-xs text-emerald-100/60">{LABEL[m.booking.status] || m.booking.status}</span>
                      </div>
                      <div className="mt-1 text-xs text-emerald-100/70">{m.booking.date} · {m.booking.time} · {m.booking.service_label}</div>
                    </button>
                  </li>
                ))}
              </ul>
              <Button variant="ghost" onClick={() => { setMatches(null); }} data-testid="back-to-search-btn" className="h-10 w-full rounded-full">
                Search again
              </Button>
            </div>
          )}

          {found && (
            <div className="space-y-5" data-testid="edit-booking-details">
              <div className="rounded-2xl border border-emerald-300/20 bg-black/20 p-4 text-sm">
                <p className="text-emerald-50"><span className="text-emerald-100/60">Ref:</span> {found.booking.ref}</p>
                <p className="text-emerald-50"><span className="text-emerald-100/60">Name:</span> {found.booking.name}</p>
                <p className="text-emerald-50"><span className="text-emerald-100/60">Appointment:</span> {found.booking.date} at {found.booking.time}</p>
                <p className="text-emerald-50"><span className="text-emerald-100/60">Status:</span> {LABEL[found.booking.status] || found.booking.status}</p>
              </div>

              {isUnpaid && bookingForPay && (
                <div className="space-y-3" data-testid="self-pay-section">
                  <p className="rounded-xl border border-amber-300/40 bg-amber-300/10 p-3 text-xs text-amber-100">
                    Your payment is not completed yet. Please pay now so we can prepare the WhatsApp link and inform the doctor.
                  </p>
                  <PaymentPanel booking={bookingForPay} onDone={onPaid} allowSkip={false} />
                </div>
              )}

              {canEdit && (
                <>
                  <div className="space-y-3">
                    <p className="flex items-center gap-2 font-display text-lg text-white"><CalendarClock className="h-5 w-5 text-emerald-300" /> {t("reschedule")}</p>
                    <SlotPicker key={slotKey} date={date} time={time} onDate={(d) => { setDate(d); setTime(""); }} onTime={setTime} serviceId={found.booking.service_id} />
                    <Button data-testid="reschedule-btn" disabled={busy || !date || !time} onClick={doReschedule} className="h-11 w-full rounded-full bg-emerald-400 text-emerald-950 hover:bg-emerald-300">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("reschedule")}
                    </Button>
                  </div>
                  <Button variant="destructive" data-testid="self-cancel-btn" onClick={doCancel} disabled={busy} className="h-11 w-full rounded-full">
                    <XCircle className="mr-2 h-4 w-4" /> {t("cancelBooking")}
                  </Button>
                </>
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
